import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon, PlusIcon, XIcon } from '@persona-id/design-tokens/icons';
import { Key, useCallback, useEffect, useState, VFC } from 'react';
import { components, SingleValueProps } from 'react-select';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MatchRequirementStub, SandboxForcedStatus } from 'dashboard/models/simulate/stubs';
import { MatchRequirementsCheckConfig } from 'dashboard/models/verification-check';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { RunVerificationCheckStub, StepOverride } from 'dashboard/models/workflow-run-simulated';
import { WorkflowStepAction, WorkflowStepActionKey } from 'dashboard/models/workflow-version';
import { StepOverrideBaseProps } from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/StepOverrideBaseProps';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { CHECK_DISPLAY_CONFIG, CHECK_REASON_MAPPING } from 'lib/constants/check';
import { ApiEndpoint } from 'lib/interfaces/api';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

interface OverrideRunVerificationProps extends StepOverrideBaseProps {}

export enum StatusSelection {
  Passed = SandboxForcedStatus.Passed,
  Failed = SandboxForcedStatus.Failed,
  Custom = 'custom',
}

const StyledLabel = styled(Label)`
  display: inline-block;
  font-weight: ${({ theme }) => theme.text.paragraph.mdBold.fontWeight};
  margin-bottom: 0px;
`;

const PassFailConfigContainer = styled('div')`
  padding: ${Spacing[2]} ${Spacing[5]} ${Spacing[2]} ${Spacing[1]};
  flex: auto;
  overflow-y: auto;
  margin-top: 1px;
`;
const StyledCard = styled(Card)`
  position: relative;
  border: solid;
  border-color: ${Color.BorderHairline};
  border-width: 1px;
`;
const StyledHeaderButton = styled(Button)`
  position: absolute;
  border: solid;
  border-color: ${Color.BorderHairline};
  border-width: 1px;
  border-radius: 50%;
  top: -10px;
  right: -10px;
  height: 20px;
  width: 20px;
  svg {
    height: 12px;
    width: 12px;
  }
`;
const CheckStubContainer = styled.div`
  display: grid;
  align-items: center;
  row-gap: ${Spacing[4]};
`;
const SelectContainer = styled.div`
  display: grid;
  row-gap: ${Spacing[2]};
`;
const StyledReactSelect = styled(ReactSelect<CheckName>)`
  margin-bottom: 0px;
`;
const CheckOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
`;
const GroupingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[2]};
`;

const getVerificationTemplateVersion = (step: WorkflowStepAction) => {
  if (step.action && step.action.key === WorkflowStepActionKey.RunVerificationGeneric) {
    return step.action.params['verification-template-version-id'] as string;
  }
  return null;
};

const getVerificationTemplateVersionType = (step: WorkflowStepAction) => {
  if (step.action && step.action.key === WorkflowStepActionKey.RunVerificationGeneric) {
    return step.action.params['verification-type'] as string;
  }
  return null;
};

const getInitialStatusStub = (simulateContext: StepOverride | undefined) => {
  if (simulateContext != null && 'status-stub' in simulateContext) {
    // TODO (ali/PLF-3686): remove type casting once we add types to the step's simulate context
    return simulateContext['status-stub'] as SandboxForcedStatus;
  }
  return null;
};

const getInitialCheckStubs = (simulateContext: StepOverride | undefined) => {
  if (simulateContext != null && 'check-stubs' in simulateContext) {
    return simulateContext['check-stubs'];
  }
  return null;
};

const MATCH_REQUIREMENT_CHECK_NAMES = ['database_identity_comparison'];

const getCheckOptions = ({
  currentCheckName,
  selectedCheckNames,
  verificationChecksMap,
}: {
  currentCheckName: CheckName | undefined;
  selectedCheckNames: CheckName[] | undefined;
  verificationChecksMap: Partial<Record<CheckName, VerificationCheckConfig>> | undefined;
}): OptionType<CheckName | undefined>[] => {
  const requiredOptions = [];
  const nonRequiredOptions = [];

  // If a check if currently selected in this select, we must include it in the options
  const currentCheck =
    verificationChecksMap && currentCheckName ? verificationChecksMap[currentCheckName] : null;
  if (currentCheck) {
    if (currentCheck['on-failed'] === VerificationStatus.Failed) {
      requiredOptions.push({
        value: currentCheck.check,
        label: CHECK_DISPLAY_CONFIG[currentCheck.check].name,
      });
    } else {
      nonRequiredOptions.push({
        value: currentCheck.check,
        label: CHECK_DISPLAY_CONFIG[currentCheck.check].name,
      });
    }
  }

  const nonSelectedChecks = Object.values(verificationChecksMap || []).filter(
    // Ensure that we have the display names and check reasons available. Or else we will crash if the user
    // attempts to select
    (verificationCheck) =>
      selectedCheckNames && !selectedCheckNames.includes(verificationCheck.check),
  );
  const requiredChecks = nonSelectedChecks.filter(
    (verificationCheck) => verificationCheck['on-failed'] === VerificationStatus.Failed,
  );
  const nonRequiredChecks = nonSelectedChecks.filter(
    (verificationCheck) => verificationCheck['on-failed'] !== VerificationStatus.Failed,
  );

  // Next, include all required checks that are not already selected in other selects for the verification
  requiredOptions.push(
    ...requiredChecks
      .filter((verificationCheck) => {
        const checkConfig = CHECK_DISPLAY_CONFIG[verificationCheck.check];
        return checkConfig && checkConfig.name;
      })
      .map((verificationCheck) => ({
        value: verificationCheck.check,
        label: CHECK_DISPLAY_CONFIG[verificationCheck.check].name,
      })),
  );

  // Lastly, include all non-required checks that are not already selected in other selects for the verification
  nonRequiredOptions.push(
    ...nonRequiredChecks
      .filter((verificationCheck) => {
        const checkConfig = CHECK_DISPLAY_CONFIG[verificationCheck.check];
        return checkConfig && checkConfig.name;
      })
      .map((verificationCheck) => ({
        value: verificationCheck.check,
        label: CHECK_DISPLAY_CONFIG[verificationCheck.check].name,
      })),
  );

  return [
    {
      label: (
        <GroupingContainer>
          Required
          <Tooltip iconTrigger={<CircleHelpIcon />} portal={true} side='top'>
            <Paragraph style={{ maxWidth: '250px' }}>
              Failing a Required check will cause the Verification to fail
            </Paragraph>
          </Tooltip>
        </GroupingContainer>
      ),
      options: requiredOptions,
      value: undefined,
    },
    {
      label: 'Not Required',
      options: nonRequiredOptions,
      value: undefined,
    },
  ];
};

const RequirementBadgedSingleValue = ({
  children,
  ...props
}: SingleValueProps<OptionType<CheckName>>) => {
  return (
    <components.SingleValue {...props}>
      <CheckOptionContainer>
        {children}
        {props.selectProps.isSelectedOptionRequired && (
          <Sticker variant='caution'>Required</Sticker>
        )}
      </CheckOptionContainer>
    </components.SingleValue>
  );
};

const CHECK_STUBS_SUPPORTED_VERIFICATIONS = [
  'verification/government-id',
  'verification/selfie',
  'verification/database',
  'verification/document',
  'verification/database-standard',
];

const OverrideRunVerification: VFC<OverrideRunVerificationProps> = ({
  step,
  simulateContext,
  setSimulateContext,
  disabled,
}) => {
  const [checkStubs, setCheckStubs] = useState<RunVerificationCheckStub[]>(() => {
    const initialCheckStubs = getInitialCheckStubs(simulateContext.overrides?.steps?.[step.token]);
    if (initialCheckStubs) {
      return initialCheckStubs.map((checkStub) => {
        if ('match-requirement-stubs' in checkStub) {
          return {
            name: checkStub.name,
            matchRequirementStubs: checkStub['match-requirement-stubs'] as MatchRequirementStub[],
          };
        } else {
          return {
            name: checkStub.name,
            reasons: checkStub.reasons,
          };
        }
      });
    } else {
      return [];
    }
  });
  const [verificationChecksMap, setVerificationCheckMap] =
    useState<Partial<Record<CheckName, VerificationCheckConfig>>>();
  const [statusStub, setStatusStub] = useState<StatusSelection>(() => {
    const initialStatus = getInitialStatusStub(simulateContext.overrides?.steps?.[step.token]);
    if (initialStatus === SandboxForcedStatus.Passed) {
      return StatusSelection.Passed;
    } else if (initialStatus === SandboxForcedStatus.Failed) {
      return StatusSelection.Failed;
    } else if (getInitialCheckStubs(simulateContext.overrides?.steps?.[step.token])) {
      return StatusSelection.Custom;
    } else {
      return StatusSelection.Passed;
    }
  });
  const verificationTemplateVersion = getVerificationTemplateVersion(step);
  const verificationTemplateVersionType = getVerificationTemplateVersionType(step);
  const fetchVerificationTemplateVersion = useCallback(
    async (versionToken: string) => {
      const { data } = await SessionClientDashboard.getResource<VerificationTemplateVersion>(
        ApiEndpoint.VerificationTemplateVersion,
        versionToken,
      );
      setVerificationCheckMap(
        Object.fromEntries(
          data.attributes['verification-checks'].map((item) => [item.check, item]),
        ),
      );
    },
    [setVerificationCheckMap],
  );

  useEffect(() => {
    if (verificationTemplateVersion) {
      fetchVerificationTemplateVersion(verificationTemplateVersion);
    }
  }, [fetchVerificationTemplateVersion, verificationTemplateVersion]);

  const handleCheckStubUpdate = (checkStubs: RunVerificationCheckStub[]) => {
    setCheckStubs(checkStubs);
    setSimulateContext({
      ...simulateContext,
      overrides: {
        ...simulateContext.overrides,
        steps: {
          ...simulateContext.overrides?.steps,
          [step.token]: {
            'check-stubs': checkStubs
              .filter((checkStub) =>
                checkStub.name && MATCH_REQUIREMENT_CHECK_NAMES.includes(checkStub.name)
                  ? checkStub.matchRequirementStubs
                  : checkStub.reasons,
              )
              .map((checkStub: RunVerificationCheckStub) => {
                const checkName = checkStub.name!;
                if (MATCH_REQUIREMENT_CHECK_NAMES.includes(checkName)) {
                  return {
                    name: checkName,
                    matchRequirementStubs: checkStub.matchRequirementStubs,
                  };
                } else {
                  const checkReason = checkStub.reasons ? [checkStub.reasons[0]] : [];
                  return {
                    name: checkName,
                    status: 'failed',
                    reasons: checkReason,
                  };
                }
              }),
          },
        },
      },
    });
  };

  const handleStatusStubUpdate = (statusSelection: StatusSelection) => {
    setStatusStub(statusSelection);

    if (statusSelection === StatusSelection.Custom) {
      setCheckStubs([]);
      setSimulateContext({
        ...simulateContext,
        overrides: {
          ...simulateContext.overrides,
          steps: {
            ...simulateContext.overrides?.steps,
            [step.token]: {
              'check-stubs': [],
            },
          },
        },
      });
    } else if (statusSelection === StatusSelection.Passed) {
      setSimulateContext({
        ...simulateContext,
        overrides: {
          ...simulateContext.overrides,
          steps: {
            ...simulateContext.overrides?.steps,
            [step.token]: {
              'status-stub': SandboxForcedStatus.Passed,
            },
          },
        },
      });
    } else {
      setSimulateContext({
        ...simulateContext,
        overrides: {
          ...simulateContext.overrides,
          steps: {
            ...simulateContext.overrides?.steps,
            [step.token]: {
              'status-stub': SandboxForcedStatus.Failed,
            },
          },
        },
      });
    }
  };

  const CheckStubsConfig = () => {
    const selectedCheckNames = checkStubs
      .map((checkStub) => checkStub.name)
      .filter((checkStub): checkStub is CheckName => checkStub != null);

    return (
      <>
        {checkStubs.map((checkStub, checkStubIndex: Key) => {
          const checkName = checkStub.name;
          return (
            <div key={checkStubIndex}>
              <StyledCard>
                <StyledHeaderButton
                  width='icon'
                  onClick={() => {
                    const updatedCheckStubs = checkStubs.filter(
                      (_checkStub, idx: Key) => idx !== checkStubIndex,
                    );
                    handleCheckStubUpdate(updatedCheckStubs);
                  }}
                  aria-label='Close'
                  variant='inverse'
                  disabled={disabled}
                  id='run-verification-override__remove-check-result'
                >
                  <XIcon />
                </StyledHeaderButton>
                <Card.Body>
                  <CheckStubContainer>
                    <SelectContainer>
                      <StyledLabel
                        htmlFor={`failed-check-${checkStubIndex}-${step.token}-input`}
                        size='sm'
                      >
                        Check Name
                      </StyledLabel>
                      <StyledReactSelect
                        id={`failed-check-${checkStubIndex}-${step.token}`}
                        value={checkName}
                        placeholder='Select a check'
                        components={{ SingleValue: RequirementBadgedSingleValue }}
                        isSelectedOptionRequired={
                          checkName &&
                          verificationChecksMap &&
                          verificationChecksMap[checkName]?.['on-failed'] ===
                            VerificationStatus.Failed
                        }
                        portal={true}
                        options={getCheckOptions({
                          currentCheckName: checkName,
                          selectedCheckNames: selectedCheckNames,
                          verificationChecksMap: verificationChecksMap,
                        })}
                        hasError={checkName == null}
                        onChange={(checkName: CheckName) => {
                          const updatedCheckStubs = checkStubs.map((checkStub, idx: Key) => {
                            if (idx === checkStubIndex) {
                              if (MATCH_REQUIREMENT_CHECK_NAMES.includes(checkName)) {
                                return {
                                  name: checkName,
                                  matchRequirementStubs: [],
                                };
                              } else {
                                return {
                                  name: checkName,
                                  reasons: [(CHECK_REASON_MAPPING[checkName]?.Failed || [])[0]],
                                };
                              }
                            }
                            return checkStub;
                          });
                          handleCheckStubUpdate(updatedCheckStubs);
                        }}
                        disabled={disabled}
                      />
                    </SelectContainer>
                    <SelectContainer>
                      <StyledLabel size='sm'>Status</StyledLabel>
                      Failed
                    </SelectContainer>
                    {checkStub.reasons && checkStub.reasons?.length > 0 && (
                      <SelectContainer>
                        <StyledLabel
                          htmlFor={`reason-${checkStubIndex}-${checkName}-input`}
                          size='sm'
                        >
                          Failure Reason
                        </StyledLabel>
                        <StyledReactSelect
                          id={`reason-${checkStubIndex}-${checkName}`}
                          value={checkStub.reasons?.[0]}
                          placeholder='Select failure reason'
                          portal={true}
                          // A bit of a convoluted way to get just failed check reasons
                          // (and their descriptions). First we use `CHECK_REASON_MAPPING` to
                          // find failed check reasons for a given check name. Then we use
                          // `CHECK_DISPLAY_CONFIG` to map the check name to the long-form
                          // description to use a labelling in the select
                          options={
                            checkName &&
                            (CHECK_REASON_MAPPING[checkName]?.Failed || []).map((option) => ({
                              value: option,
                              // Attempt to map to the long-form description; otherwise fallback
                              // to the programmatic check name
                              label: CHECK_DISPLAY_CONFIG[checkName].reasons[option] || option,
                            }))
                          }
                          onChange={(value: string) => {
                            const updatedCheckStubs = checkStubs.map((checkStub, idx: Key) => {
                              if (idx === checkStubIndex) {
                                return {
                                  name: checkName,
                                  reasons: [value],
                                };
                              }
                              return checkStub;
                            });
                            handleCheckStubUpdate(updatedCheckStubs);
                          }}
                          disabled={disabled}
                        />
                      </SelectContainer>
                    )}
                    {checkStub.matchRequirementStubs && verificationChecksMap && checkName && (
                      <SelectContainer>
                        <StyledLabel
                          htmlFor={`match-requirements-${checkStubIndex}-${checkName}-input`}
                          size='sm'
                        >
                          Failed Match Requirements
                        </StyledLabel>
                        <StyledReactSelect
                          id={`match-requirements-${checkStubIndex}-${checkName}`}
                          value={checkStub.matchRequirementStubs.map(
                            (stub: MatchRequirementStub) => stub.name,
                          )}
                          placeholder='Select failed match requirements'
                          portal={true}
                          options={(
                            verificationChecksMap[checkName] as MatchRequirementsCheckConfig
                          )['check-requirements'].map((matchRequirement) => ({
                            // Still `check-requirements` here since API interface not yet updated
                            value: matchRequirement.name,
                            label: matchRequirement.description,
                          }))}
                          onChange={(selectedFailedMatchRequirements: string[]) => {
                            const newMatchRequirementStubs = selectedFailedMatchRequirements.map(
                              (matchRequirementName) => ({
                                name: matchRequirementName,
                                status: 'failed',
                              }),
                            );
                            const updatedCheckStubs = checkStubs.map((checkStub, idx: Key) => {
                              if (idx === checkStubIndex) {
                                return {
                                  name: checkStub.name,
                                  matchRequirementStubs: newMatchRequirementStubs.map((stub) => ({
                                    name: stub.name as CheckName,
                                    status: stub.status || 'failed',
                                  })),
                                };
                              }
                              return checkStub;
                            });
                            handleCheckStubUpdate(updatedCheckStubs);
                          }}
                          disabled={disabled}
                          isMulti
                        />
                      </SelectContainer>
                    )}
                  </CheckStubContainer>
                </Card.Body>
              </StyledCard>
            </div>
          );
        })}
        <Button
          width='auto'
          variant='basic'
          disabled={disabled}
          id='run-verification-override__add-check-result'
          onClick={() => {
            setCheckStubs((currentCheckStubs) => [
              ...currentCheckStubs,
              {
                name: undefined,
                reasons: [],
              },
            ]);
          }}
        >
          <PlusIcon /> Check result
        </Button>
      </>
    );
  };
  return (
    <>
      <PassFailConfigContainer>
        <StyledLabel htmlFor='pass-fail-config-input' size='md'>
          Verification results
        </StyledLabel>
        <Spacer height={1} />
        <RadioGroup id='pass-fail-config' onChange={handleStatusStubUpdate} value={statusStub}>
          <Radio
            key='verifications-pass'
            label='Pass'
            value={StatusSelection.Passed}
            disabled={disabled}
          />
          <Radio
            key='verifications-fail'
            label='Fail'
            value={StatusSelection.Failed}
            disabled={disabled}
          />
          {verificationTemplateVersionType &&
            CHECK_STUBS_SUPPORTED_VERIFICATIONS.includes(verificationTemplateVersionType) && (
              <Radio
                key='verifications-custom'
                label='Custom'
                value={StatusSelection.Custom}
                disabled={disabled}
              />
            )}
        </RadioGroup>
        {statusStub === StatusSelection.Custom && (
          <>
            Configure custom check results
            <Spacer height={1} />
            <CheckStubsConfig />
          </>
        )}
      </PassFailConfigContainer>
    </>
  );
};

export default OverrideRunVerification;
